import { useEffect, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { api, fmt, fmtDate, toDateInput, getUser, ORDER_STATUS, stBadge } from '../api';
import { Field, Input, useToast, Loading, Eyebrow } from '../ui';
import { Icons } from '../icons';

interface Line {
  id?: number; productId: number; article: string; name: string;
  boxQty: number; boxKg: number; boxM3: number; unitKg: number; unitM3: number;
  inputMode: string; qtyUnits: number; qtyBoxes: number; factoryPrice: number; currency: string;
}

export default function OrderEdit() {
  const { id } = useParams();
  const nav = useNavigate();
  const toast = useToast();
  const user = getUser();
  const canEdit = ['admin', 'zakaz_operator'].includes(user?.role);

  const [order, setOrder] = useState<any>(null);
  const [suppliers, setSuppliers] = useState<any[]>([]);
  const [currencies, setCurrencies] = useState<any[]>([]);
  const [lines, setLines] = useState<Line[]>([]);
  const [search, setSearch] = useState('');
  const [results, setResults] = useState<any[]>([]);
  const fileRef = useRef<HTMLInputElement>(null);
  const locked = order && !['draft', 'confirmed'].includes(order.status);

  // Saqlanmagan o'zgarish bor-yo'qligi — sahifadan chiqishda ogohlantirish uchun
  const [dirty, setDirty] = useState(false);
  useEffect(() => {
    if (!dirty) return;
    const h = (e: BeforeUnloadEvent) => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', h);
    return () => window.removeEventListener('beforeunload', h);
  }, [dirty]);
  const leave = (to: string) => {
    if (dirty && !confirm("Saqlanmagan o'zgarishlar bor. Baribir chiqasizmi?")) return;
    nav(to);
  };

  const load = () => api.get('/orders/' + id).then((o) => {
    setDirty(false);
    setOrder(o);
    setLines(o.lines.map((l: any) => ({
      id: l.id, productId: l.productId, article: l.article, name: l.name,
      boxQty: Number(l.boxQty) || 0, boxKg: Number(l.boxKg) || 0, boxM3: Number(l.boxM3) || 0,
      unitKg: Number(l.unitKg) || 0, unitM3: Number(l.unitM3) || 0,
      inputMode: l.inputMode, qtyUnits: l.qtyUnits, qtyBoxes: l.qtyBoxes, factoryPrice: l.factoryPrice,
      currency: l.currency || o.currency || 'CNY',
    })));
  });
  useEffect(() => { load(); api.get('/suppliers').then(setSuppliers); api.get('/currencies').then(setCurrencies); }, [id]);

  useEffect(() => {
    if (search.length < 2) { setResults([]); return; }
    const t = setTimeout(() => api.get('/products?search=' + encodeURIComponent(search) + '&pageSize=8').then((d) => setResults(d.items)), 250);
    return () => clearTimeout(t);
  }, [search]);

  const addLine = (p: any) => {
    setDirty(true);
    setLines([...lines, {
      productId: p.id, article: p.article, name: p.name,
      boxQty: Number(p.boxQty) || 0, boxKg: Number(p.boxGrossKg) || 0, boxM3: Number(p.boxM3) || 0,
      unitKg: Number(p.unitNetKg) || 0, unitM3: Number(p.unitM3) || 0,
      inputMode: 'boxes', qtyUnits: 0, qtyBoxes: 0, factoryPrice: p.lastFactoryPrice || 0,
      currency: order?.currency || 'CNY',
    }]);
    setSearch(''); setResults([]);
  };
  const curSym = (code: string) => currencies.find((c) => c.code === code)?.symbol || code;
  const setAllCurrency = (code: string) => { setDirty(true); setOrder({ ...order, currency: code }); setLines(lines.map((l) => ({ ...l, currency: code }))); };
  const upd = (i: number, patch: Partial<Line>) => { setDirty(true); setLines(lines.map((l, idx) => idx === i ? { ...l, ...patch } : l)); };
  const del = (i: number) => { setDirty(true); setLines(lines.filter((_, idx) => idx !== i)); };
  const updHead = (patch: any) => { setDirty(true); setOrder({ ...order, ...patch }); };

  // Katalogdan hisoblangan default korobka qiymatlari (dona og'irligi/hajmi × korobkadagi soni)
  const defBoxKg = (l: Line) => (l.unitKg > 0 && l.boxQty > 0 ? Math.round(l.unitKg * l.boxQty * 1000) / 1000 : 0);
  const defBoxM3 = (l: Line) => (l.unitM3 > 0 && l.boxQty > 0 ? Math.round(l.unitM3 * l.boxQty * 10000) / 10000 : 0);

  // Backend lineCalc bilan AYNAN bir xil formula — ro'yxat va detal jamilari mos bo'ladi
  const calc = (l: Line) => {
    const units = l.inputMode === 'boxes' ? Number(l.qtyBoxes) * Number(l.boxQty) : Number(l.qtyUnits);
    const boxes = l.inputMode === 'boxes' ? Number(l.qtyBoxes) : (l.boxQty > 0 ? Number(l.qtyUnits) / Number(l.boxQty) : 0);
    const kg = l.boxKg > 0 && boxes > 0 ? boxes * Number(l.boxKg) : units * Number(l.unitKg);
    const m3 = l.boxM3 > 0 && boxes > 0 ? boxes * Number(l.boxM3) : units * Number(l.unitM3);
    return { units, boxes, kg, m3, sum: units * Number(l.factoryPrice || 0) };
  };
  const totals = lines.reduce(
    (a, l) => {
      const c = calc(l);
      a.units += c.units; a.boxes += c.boxes; a.kg += c.kg; a.m3 += c.m3;
      if (c.sum > 0) a.sums[l.currency] = (a.sums[l.currency] || 0) + c.sum;
      return a;
    },
    { units: 0, boxes: 0, kg: 0, m3: 0, sums: {} as Record<string, number> },
  );
  const totalSumsText = Object.keys(totals.sums).length === 0 ? '0'
    : Object.entries(totals.sums).map(([c, v]) => `${fmt(v, 2)} ${curSym(c)}`).join(' + ');

  const save = async () => {
    try {
      await api.patch('/orders/' + id, {
        supplierId: order.supplierId, orderDate: order.orderDate, note: order.note, currency: order.currency,
        fxRate: order.fxRate === '' ? null : order.fxRate,
        items: lines.map((l, i) => ({
          id: l.id, // mavjud qator saqlanadi — kirim/rastamojka bog'lari uzilmaydi
          productId: l.productId, lineNo: i + 1, inputMode: l.inputMode,
          qtyUnits: l.qtyUnits, qtyBoxes: l.qtyBoxes,
          boxQtyOvr: l.boxQty || null, boxKgOvr: l.boxKg || null, boxM3Ovr: l.boxM3 || null,
          factoryPrice: l.factoryPrice, currency: l.currency,
        })),
      });
      toast('Saqlandi'); load();
    } catch (e: any) { toast(e.message, true); }
  };
  const setStatus = async (status: string) => {
    if (dirty && !confirm("Saqlanmagan o'zgarishlar bor — ular yo'qoladi. Davom etasizmi?")) return;
    let note; if (status === 'cancelled') { note = prompt('Zakazni bekor qilish sababi:'); if (!note) return; }
    try { await api.post(`/orders/${id}/status`, { status, note }); toast('Status yangilandi'); load(); }
    catch (e: any) { toast(e.message, true); }
  };
  const deleteOrder = async () => {
    if (!confirm("Bu zakaz butunlay o'chirilsinmi?")) return;
    try { await api.del('/orders/' + id); toast("O'chirildi"); nav('/orders'); }
    catch (e: any) { toast(e.message, true); }
  };
  const doImport = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]; if (!file) return;
    try { const r = await api.upload(`/orders/${id}/import`, file); toast(`${r.added} qator qo'shildi, ${r.skipped} o'tkazildi`); load(); }
    catch (e: any) { toast(e.message, true); }
    if (fileRef.current) fileRef.current.value = '';
  };

  if (!order) return <Loading />;

  return (
    <div>
      <div className="page-head">
        <div>
          <h1 className="page-title">{order.number}</h1>
          <p className="page-sub">
            <span className={'badge ' + stBadge(ORDER_STATUS, order.status).cls}>{stBadge(ORDER_STATUS, order.status).label}</span>
            {dirty && <span className="badge b-partial" style={{ marginLeft: 6 }}>● Saqlanmagan</span>}
          </p>
        </div>
        <div className="head-actions">
          <button className="btn" onClick={() => leave('/orders')}>← Ro'yxat</button>
          <button className="btn" title="Zavodga yuboriladigan Excel fayl"
            onClick={() => api.download(`/orders/${id}/export`, `${order.number}.xlsx`)}>
            <Icons.download size={16} /> Zavodga Excel
          </button>
          {canEdit && <button className="btn" title="Shu assortiment bilan yangi zakaz ochish"
            onClick={async () => { try { const c = await api.post(`/orders/${id}/duplicate`); toast('Nusxa yaratildi: ' + c.number); nav('/orders/' + c.id); } catch (e: any) { toast(e.message, true); } }}>
            ⧉ Nusxa olish
          </button>}
          {canEdit && !locked && <>
            <input ref={fileRef} type="file" accept=".xlsx" hidden onChange={doImport} />
            <button className="btn" onClick={() => fileRef.current?.click()}><Icons.upload size={16} /> Invoys import</button>
            <button className="btn btn-primary" onClick={save}>Saqlash</button>
          </>}
          {canEdit && order.status === 'draft' && <button className="btn" onClick={() => setStatus('confirmed')}>Tasdiqlash</button>}
          {canEdit && order.status === 'confirmed' && <button className="btn" onClick={() => setStatus('in_transit')}>Yo'lda jo'natildi</button>}
          {/* Status tanlash (admin/operator uchun har qanday statusga) */}
          {canEdit && <select className="input" style={{ width: 170 }} value={order.status}
            onChange={(e) => e.target.value !== order.status && setStatus(e.target.value)}>
            {Object.entries(ORDER_STATUS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
          </select>}
          {/* Bekor qilish (sabab bilan) — faol zakaz uchun */}
          {canEdit && !['draft', 'cancelled', 'closed'].includes(order.status) && <button className="btn" onClick={() => setStatus('cancelled')}>Bekor qilish</button>}
          {/* O'chirish — har qanday statusda */}
          {canEdit && <button className="btn btn-danger" onClick={deleteOrder}>🗑 O'chirish</button>}
        </div>
      </div>

      <Eyebrow title="Zakaz ma'lumotlari" desc="zavod, sana va hujjat valyutasi" />
      <div className="card" style={{ marginBottom: 6 }}>
        <div className="grid-3">
          <Field label="Zavod">
            <select className="input" disabled={locked} value={order.supplierId || ''} onChange={(e) => updHead({ supplierId: e.target.value ? +e.target.value : null })}>
              <option value="">—</option>
              {suppliers.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
          </Field>
          <Field label="Sana"><Input type="date" disabled={locked} value={toDateInput(order.orderDate)} onChange={(e) => updHead({ orderDate: e.target.value })} /></Field>
          <Field label="Valyuta (barcha qatorlar uchun)">
            <select className="input" disabled={locked} value={order.currency || 'CNY'} onChange={(e) => setAllCurrency(e.target.value)}>
              {currencies.map((c) => <option key={c.id} value={c.code}>{c.code} — {c.name}</option>)}
            </select>
          </Field>
          <Field label={`Kurs (1 ${order.currency || 'CNY'} = ? USD, ixtiyoriy)`}>
            <Input type="number" step="0.000001" disabled={locked} placeholder="masalan 0.1481"
              value={order.fxRate ?? ''} onChange={(e) => updHead({ fxRate: e.target.value === '' ? null : e.target.value })} />
          </Field>
          <Field label="Izoh"><Input disabled={locked} value={order.note || ''} onChange={(e) => updHead({ note: e.target.value })} /></Field>
        </div>
      </div>

      <Eyebrow title="Pozitsiyalar" desc="Mesta × Korobkada = Dona · kilo/kuba korobkaga kiritiladi" />
      {canEdit && !locked && (
        <div className="card" style={{ marginBottom: 12, padding: 12 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <b style={{ whiteSpace: 'nowrap' }}>➕ Mahsulot qo'shish:</b>
            <div style={{ position: 'relative', flex: 1, minWidth: 260 }}>
              <input className="search" style={{ maxWidth: '100%', width: '100%' }} autoFocus
                placeholder="Artikul yoki nom yozing (masalan: 14-3-10-0001 yoki Panel Akril)…"
                value={search} onChange={(e) => setSearch(e.target.value)} />
              {search.length >= 2 && (
                <div className="card" style={{ position: 'absolute', top: 44, left: 0, right: 0, zIndex: 20, padding: 6, maxHeight: 320, overflowY: 'auto' }}>
                  {results.length === 0 ? <div className="muted" style={{ padding: 10 }}>Topilmadi…</div> :
                    results.map((p) => (
                      <div key={p.id} className="row-card" style={{ margin: 4, padding: '8px 12px', cursor: 'pointer' }} onClick={() => addLine(p)}>
                        <div><div className="row-title">{p.name}</div><div className="row-sub">{p.article} · {p.brand?.name || ''}{p.boxQty ? ` · korobkada ${p.boxQty}` : ''}</div></div>
                        <span className="btn btn-sm btn-primary">+ qo'shish</span>
                      </div>
                    ))}
                </div>
              )}
            </div>
            <span className="muted" style={{ fontSize: 12 }}>yoki ⬆ Zavod invoysi import (Excel) bilan ko'p qatorni birdan</span>
          </div>
        </div>
      )}

      <div className="table-wrap">
        <table>
          <thead><tr>
            <th>Artikul</th><th>Nomi</th>
            <th className="num">Mesta</th><th className="num">Korobkada</th><th className="num">Dona</th>
            <th className="num">Kuba m³</th><th className="num">Jami m³</th>
            <th className="num">Kilo kg</th><th className="num">Jami kg</th>
            <th className="num">Narx</th><th>Val.</th><th className="num">Summa</th><th></th>
          </tr></thead>
          <tbody>
            {lines.map((l, i) => {
              const c = calc(l);
              return (
                <tr key={i}>
                  <td>{l.article}</td><td>{l.name}</td>
                  <td className="num"><input className="input num cell-input" type="number" min="0" disabled={locked}
                    value={l.qtyBoxes || ''} onChange={(e) => upd(i, { inputMode: 'boxes', qtyBoxes: Math.max(0, +e.target.value) })} /></td>
                  <td className="num"><input className="input num cell-input" type="number" min="0" disabled={locked}
                    value={l.boxQty || ''} onChange={(e) => upd(i, { boxQty: Math.max(0, +e.target.value) })} /></td>
                  <td className="num" style={{ fontWeight: 600 }}>{fmt(c.units)}</td>
                  {/* Kuba/Kilo: bo'sh bo'lsa katalogdan hisoblangan default ko'rinadi (kulrang), ustiga yozsa o'z qiymati */}
                  <td className="num"><input className={'input num cell-input' + (!l.boxM3 && defBoxM3(l) ? ' auto' : '')} type="number" min="0" step="0.001" disabled={locked}
                    value={l.boxM3 || defBoxM3(l) || ''} onChange={(e) => upd(i, { boxM3: Math.max(0, +e.target.value) })} /></td>
                  <td className="num">{fmt(c.m3, 3)}</td>
                  <td className="num"><input className={'input num cell-input' + (!l.boxKg && defBoxKg(l) ? ' auto' : '')} type="number" min="0" step="0.01" disabled={locked}
                    value={l.boxKg || defBoxKg(l) || ''} onChange={(e) => upd(i, { boxKg: Math.max(0, +e.target.value) })} /></td>
                  <td className="num">{fmt(c.kg, 2)}</td>
                  <td className="num"><input className="input num cell-input" type="number" min="0" step="0.01" disabled={locked} value={l.factoryPrice || ''} onChange={(e) => upd(i, { factoryPrice: Math.max(0, +e.target.value) })} /></td>
                  <td><select className="input cell-sel" disabled={locked} value={l.currency} onChange={(e) => upd(i, { currency: e.target.value })}>
                    {currencies.map((cur) => <option key={cur.id} value={cur.code}>{cur.code}</option>)}
                  </select></td>
                  <td className="num" style={{ fontWeight: 600 }}>{fmt(c.sum, 2)} {curSym(l.currency)}</td>
                  <td>{canEdit && !locked && <button className="act act-del" onClick={() => del(i)}><Icons.trash size={15} /></button>}</td>
                </tr>
              );
            })}
            {lines.length === 0 && <tr><td colSpan={13} className="empty">Pozitsiyalar yo'q</td></tr>}
          </tbody>
          {lines.length > 0 && (
            <tfoot>
              <tr className="total-row">
                <td colSpan={2}>JAMI</td>
                <td className="num">{fmt(totals.boxes, 1)}</td><td></td><td className="num">{fmt(totals.units)}</td>
                <td></td><td className="num">{fmt(totals.m3, 3)}</td>
                <td></td><td className="num">{fmt(totals.kg, 2)}</td>
                <td></td><td></td><td className="num">{totalSumsText}</td><td></td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>

      {order.statusHistory?.length > 0 && (
        <>
          <Eyebrow title="Status tarixi" desc="kim qachon o'zgartirgani" />
          <div className="card">
            {order.statusHistory.map((h: any) => (
              <div key={h.id} className="muted" style={{ fontSize: 13, padding: '3px 0' }}>
                {fmtDate(h.changedAt)} — {ORDER_STATUS[h.status]?.label} {h.note ? `(${h.note})` : ''}
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
